import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BugReportIcon from "@mui/icons-material/BugReport";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import TimelineIcon from "@mui/icons-material/Timeline";
import GroupIcon from "@mui/icons-material/Group";
import BarChartIcon from "@mui/icons-material/BarChart";
import { ApexOptions } from "apexcharts";
import {
  SeveritySummary,
  SkillSummary,
  StatusSummary,
  TagSummary,
  TypeSummary,
  AssigneeWorkload,
  SkillWorkload,
} from "./interface";
import { KPICard } from "./KPICard";
import { ChartCard } from "./ChartCard";
import {
  COLORS_CHART,
  formatCodeLabel,
  secondsToHours,
  SEVERITY_COLORS,
  STATUS_COLORS,
} from "./util";

interface MemberBugStatus {
  statusCode: string;
  status: string;
  count: number;
}

interface MemberWithBugs {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  isOwner: boolean;
  totalBugs: number;
  statusWiseBugs: MemberBugStatus[];
}

interface MemberStatistics {
  totalMembers: number;
  totalStatuses: number;
  roleWiseMemberCount: Record<string, number>;
  statusWiseBugCount: { _id: string; status: string; count: number }[];
  membersWithBugs: MemberWithBugs[];
}

interface KanbanDashboardProps {
  projectSummary: any;
  projectWorkloadMetrics: any;
  parentData?: any;
  totalUsers: number;
  projectMembers?: any[];
  memberStatistics?: MemberStatistics;
}

const KanbanDashboard: React.FC<KanbanDashboardProps> = ({
  projectSummary,
  projectWorkloadMetrics,
  parentData,
  totalUsers,
  projectMembers = [],
  memberStatistics,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const [memberSearch, setMemberSearch] = useState("");

  const assigneeWorkloadData = useMemo(() => {
    if (!projectWorkloadMetrics?.assigneeVsWorklod) {
      return { categories: [], timeSpent: [], estimated: [] };
    }

    const data = projectWorkloadMetrics.assigneeVsWorklod
      .filter((item: AssigneeWorkload) => item.workedBy !== null)
      .map((item: AssigneeWorkload) => ({
        name: item.workedBy?.fullName || "Unknown",
        timeSpent: secondsToHours(item.totalTimeSpend),
        estimated: secondsToHours(item.estimationInSeconds || 0),
        id: item.workedBy?._id,
      }))
      .filter((item) => item.name !== "Unknown" && item.timeSpent > 0)
      .sort((a, b) => b.timeSpent - a.timeSpent)
      .slice(0, 10);
    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [projectWorkloadMetrics]);

  const skillWorkloadData = useMemo(() => {
    if (!projectWorkloadMetrics?.skillSetVsworkload) {
      return { categories: [], timeSpent: [], estimated: [] };
    }

    const data = projectWorkloadMetrics.skillSetVsworkload
      .filter(
        (item: SkillWorkload) =>
          item.skillType !== "Not Defined" && item.totalTimeSpendSkillWise > 0,
      )
      .map((item: SkillWorkload) => ({
        name: item.skillType,
        timeSpent: secondsToHours(item.totalTimeSpendSkillWise),
        estimated: secondsToHours(item.estimationInSeconds || 0),
      }))
      .sort((a, b) => b.timeSpent - a.timeSpent);

    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [projectWorkloadMetrics]);

  // Status data
  const statusChartData = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    const result = bugsByStatus.map((item: StatusSummary) => item.total);
    return result;
  }, [projectSummary?.byStatus]);

  const statusLabels = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    const result = bugsByStatus.map((item: StatusSummary) =>
      formatCodeLabel(item.statusCode),
    );
    return result;
  }, [projectSummary?.byStatus]);

  const statusColors = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    return bugsByStatus.map(
      (item: StatusSummary) => STATUS_COLORS[item.statusCode] || "#999",
    );
  }, [projectSummary?.byStatus]);

  // Severity data
  const severityChartData = useMemo(() => {
    return (projectSummary?.bySeverity || []).map(
      (item: SeveritySummary) => item.total,
    );
  }, [projectSummary?.bySeverity]);

  const severityLabels = useMemo(() => {
    return (projectSummary?.bySeverity || []).map((item: SeveritySummary) =>
      formatCodeLabel(item.severity),
    );
  }, [projectSummary?.bySeverity]);

  const severityColors = useMemo(() => {
    return (projectSummary?.bySeverity || []).map(
      (item: SeveritySummary) => SEVERITY_COLORS[item.severity] || "#999",
    );
  }, [projectSummary?.bySeverity]);

  // Type data
  const typeChartData = useMemo(() => {
    return (projectSummary?.byType || []).map(
      (item: TypeSummary) => item.total,
    );
  }, [projectSummary?.byType]);

  const typeLabels = useMemo(() => {
    return (projectSummary?.byType || []).map((item: TypeSummary) =>
      formatCodeLabel(item.type),
    );
  }, [projectSummary?.byType]);

  // Skill data
  const skillChartData = useMemo(() => {
    if (
      !projectSummary?.byRequiredSkillSet ||
      projectSummary.byRequiredSkillSet.length === 0
    )
      return { values: [], labels: [] };

    const processedData = projectSummary.byRequiredSkillSet
      .map((item: SkillSummary) => ({
        name: formatCodeLabel(item.skillSet || "Unknown"),
        value: item.total,
      }))
      .filter(
        (item) =>
          item.name !== "Unknown" &&
          item.name !== "Not Defined" &&
          item.value > 0,
      );

    return {
      values: processedData.map((item) => item.value),
      labels: processedData.map((item) => item.name),
    };
  }, [projectSummary?.byRequiredSkillSet]);

  // Tag data
  const tagChartData = useMemo(() => {
    return (projectSummary?.byTags || [])
      .filter((t: TagSummary) => t.tags)
      .slice(0, 8)
      .map((item: TagSummary) => item.total);
  }, [projectSummary?.byTags]);

  const tagLabels = useMemo(() => {
    return (projectSummary?.byTags || [])
      .filter((t: TagSummary) => t.tags)
      .slice(0, 8)
      .map((item: TagSummary) => formatCodeLabel(item.tags));
  }, [projectSummary?.byTags]);

  // Progress data
  const progressData = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    const completedBugs =
      bugsByStatus.find((s: StatusSummary) => s.statusCode === "DONE")?.total ||
      0;
    const totalBugs = projectSummary?.totalBugs || 0;
    const remainingBugs = Math.max(0, totalBugs - completedBugs);

    return [completedBugs, remainingBugs];
  }, [projectSummary?.byStatus, projectSummary?.totalBugs]);

  const progressLabels = [t("Completed"), t("Remaining")];

  // KPIs
  const totalBugs = projectSummary?.totalBugs || 0;
  const closedBugs = projectSummary?.totalClosedBugs || 0;
  const openBugs = totalBugs - closedBugs;
  const bugsByStatus = projectSummary?.byStatus || [];
  const blockedBugs =
    bugsByStatus.find((s: StatusSummary) => s.statusCode === "BLOCKED")
      ?.total || 0;

  // ✅ Calculate metrics from KANBAN workload data
  const projectMetrics = useMemo(() => {
    if (!projectWorkloadMetrics?.assigneeVsWorklod) {
      return {
        totalTimeSpent: 0,
        totalEstimated: 0,
        totalAssignees: 0,
      };
    }

    const totalTimeSpent = projectWorkloadMetrics.assigneeVsWorklod.reduce(
      (sum: number, item: AssigneeWorkload) => sum + item.totalTimeSpend,
      0,
    );
    const totalEstimated = projectWorkloadMetrics.assigneeVsWorklod.reduce(
      (sum: number, item: AssigneeWorkload) =>
        sum + (item.estimationInSeconds || 0),
      0,
    );
    const totalAssignees = projectWorkloadMetrics.assigneeVsWorklod.filter(
      (item: AssigneeWorkload) => item.workedBy !== null,
    ).length;

    return {
      totalTimeSpent: secondsToHours(totalTimeSpent),
      totalEstimated: secondsToHours(totalEstimated),
      totalAssignees: totalAssignees,
    };
  }, [projectWorkloadMetrics]);

  const assigneeCount = useMemo(() => {
    if (!projectMembers || projectMembers.length === 0) return 0;
    return projectMembers.filter((member: any) => member.userRole !== "CLIENT")
      .length;
  }, [projectMembers]);

  const avgResolutionTime = useMemo(() => {
    if (closedBugs === 0) return 0;
    if (projectMetrics.totalTimeSpent > 0) {
      return (projectMetrics.totalTimeSpent / closedBugs).toFixed(1);
    }
    return 0;
  }, [closedBugs, projectMetrics]);

  const roleDistributionData = useMemo(() => {
    if (!projectMembers || projectMembers.length === 0) {
      return {
        labels: [] as string[],
        series: [] as number[],
        colors: [] as string[],
      };
    }
    const roleMap: Record<string, number> = {};
    projectMembers?.forEach((member: any) => {
      const role = member.userRole || "Unknown";
      roleMap[role] = (roleMap[role] || 0) + 1;
    });
    const labels = Object.keys(roleMap).map((r) => formatCodeLabel(r));
    const series = Object.values(roleMap);
    const colors = labels.map((_, i) => COLORS_CHART[i % COLORS_CHART.length]);
    return { labels, series, colors };
  }, [projectMembers]);

  // Member-wise task data (exclude clients, deduplicate, sorted by totalBugs descending)
  const membersWithBugsSorted = useMemo(() => {
    if (!memberStatistics?.membersWithBugs) return [];
    const uniqueMap = new Map<string, MemberWithBugs>();
    memberStatistics.membersWithBugs
      .filter((m) => m.userRole !== "CLIENT")
      .forEach((m) => {
        if (
          !uniqueMap.has(m.userId) ||
          m.totalBugs > (uniqueMap.get(m.userId)?.totalBugs || 0)
        ) {
          uniqueMap.set(m.userId, m);
        }
      });
    return Array.from(uniqueMap.values()).sort(
      (a, b) => b.totalBugs - a.totalBugs,
    );
  }, [memberStatistics]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return membersWithBugsSorted;
    const search = memberSearch.toLowerCase();
    return membersWithBugsSorted.filter(
      (m) =>
        m.userName?.toLowerCase().includes(search) ||
        m.userEmail?.toLowerCase().includes(search),
    );
  }, [membersWithBugsSorted, memberSearch]);

  const maxBugs = useMemo(() => {
    if (membersWithBugsSorted.length === 0) return 1;
    return Math.max(...membersWithBugsSorted.map((m) => m.totalBugs), 1);
  }, [membersWithBugsSorted]);

  // ApexChart Options
  const mainPieOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        width: "100%",
        fontFamily: "inherit",
      },
      labels: statusLabels,
      colors: statusColors,
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontWeight: 500,
        offsetY: 5,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "600",
        },
        formatter: function (val: number, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: t("Total"),
                fontSize: "14px",
                fontWeight: 600,
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0,
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      stroke: {
        width: 2,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [statusLabels, statusColors],
  );
  //@ts-ignore
  const severityBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: severityColors,
      xaxis: {
        categories: severityLabels,
        title: {
          text: t("Severity"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        title: {
          text: t("Count"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        offsetY: 5,
        markers: { width: 10, height: 10, radius: 2 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [severityLabels, severityColors],
  );
  //@ts-ignore
  const typeBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        categories: typeLabels,
        title: {
          text: t("Type"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        title: {
          text: t("Count"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        offsetY: 5,
        markers: { width: 10, height: 10, radius: 2 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [typeLabels],
  );

  const progressOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        width: "100%",
        fontFamily: "inherit",
      },
      labels: progressLabels,
      colors: ["#4CAF50", "#FFC107"],
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontWeight: 500,
        offsetY: 5,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "600",
        },
        formatter: function (val: number, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: t("Total"),
                fontSize: "14px",
                fontWeight: 600,
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0,
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      stroke: {
        width: 2,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [progressLabels],
  );
  //@ts-ignore
  const skillBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          distributed: true,
          barHeight: "75%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        title: {
          text: t("Number of Issues"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: string) {
            return Math.floor(Number(val)).toString();
          },
        },
      },
      yaxis: {
        categories: skillChartData.labels,
        labels: {
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        offsetY: 5,
        markers: {
          width: 10,
          height: 10,
          radius: 2,
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
          colors: ["#fff"],
        },
        formatter: function (val: number) {
          return Math.floor(val).toString();
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return Math.floor(val).toString() + " " + t("issues");
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
    }),
    [skillChartData.labels],
  );
  //@ts-ignore
  const tagBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        categories: tagLabels,
        title: {
          text: t("Tags"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        title: {
          text: t("Count"),
          style: { fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        offsetY: 5,
        markers: { width: 10, height: 10, radius: 2 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [tagLabels],
  );
  //@ts-ignore
  const assigneeWorkloadOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        stacked: false,
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: "75%",
        },
      },
      colors: ["#FF6B6B", "#4CAF50"],
      xaxis: {
        categories: assigneeWorkloadData.categories,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        title: {
          text: t("Hours"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 600,
        offsetY: 0,
        markers: {
          width: 14,
          height: 14,
          radius: 4,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val.toFixed(1)} hrs`,
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
    }),
    [assigneeWorkloadData.categories],
  );
  //@ts-ignore
  const skillWorkloadOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        stacked: false,
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          barHeight: "70%",
        },
      },
      colors: ["#FF6B6B", "#4CAF50"],
      xaxis: {
        title: {
          text: t("Hours"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
        },
      },
      yaxis: {
        categories: skillWorkloadData.categories,
        labels: {
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 600,
        offsetY: 0,
        markers: {
          width: 14,
          height: 14,
          radius: 4,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val.toFixed(1)} hrs`,
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
    }),
    [skillWorkloadData.categories],
  );

  const roleDistributionOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        width: "100%",
        fontFamily: "inherit",
      },
      labels: roleDistributionData.labels,
      colors: roleDistributionData.colors,
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontWeight: 500,
        offsetY: 5,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "600",
        },
        formatter: function (val: number, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: t("Total"),
                fontSize: "14px",
                fontWeight: 600,
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0,
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      stroke: {
        width: 2,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [roleDistributionData.labels, roleDistributionData.colors],
  );

  const NoDataPlaceholder = ({ height = 250 }: { height?: number }) => (
    <Box
      sx={{
        width: "100%",
        height,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {/* Faded dummy bar chart lines */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          mb: 1.5,
          opacity: 0.15,
        }}
      >
        {[40, 65, 30, 80, 50, 70, 45].map((h, i) => (
          <Box
            key={i}
            sx={{
              width: 14,
              height: h,
              borderRadius: "3px 3px 0 0",
              bgcolor: "text.primary",
            }}
          />
        ))}
      </Box>
      {/* <BarChartIcon
        sx={{ fontSize: 28, color: "text.disabled", opacity: 0.5, mb: 0.5 }}
      /> */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontSize: "12px" }}
      >
        {t("No data available")}
      </Typography>
    </Box>
  );

  // Check if dashboard data is loaded
  const hasData = projectSummary && Object.keys(projectSummary).length > 0;

  if (!hasData) {
    return (
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        {/* KPI Skeleton Row 1 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={`kpi-skel-1-${i}`}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  p: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={16}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={32} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={48}
                    height={48}
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* KPI Skeleton Row 2 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={`kpi-skel-2-${i}`}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  p: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="55%"
                      height={16}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="35%" height={32} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={48}
                    height={48}
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Chart Skeleton Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3}
              key={`chart-skel-${i}`}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  height: 280,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Skeleton
                  variant="text"
                  width="50%"
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={160}
                    height={160}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Workload Chart Skeleton */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Grid item xs={12} lg={6} key={`workload-skel-${i}`}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  height: 320,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Skeleton
                  variant="text"
                  width="45%"
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1.5,
                    px: 2,
                    pb: 2,
                  }}
                >
                  {[40, 70, 55, 85, 45, 65, 50, 75].map((h, idx) => (
                    <Skeleton
                      key={idx}
                      variant="rounded"
                      width="100%"
                      height={`${h}%`}
                      sx={{ borderRadius: "4px 4px 0 0", flex: 1 }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Table Skeleton */}
        <Grid container spacing={2} sx={{ mb: 10 }}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Skeleton
                variant="text"
                width="30%"
                height={24}
                sx={{ mb: 2 }}
              />
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={`row-skel-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1.5,
                    borderBottom: (theme) =>
                      i < 4
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                  }}
                >
                  <Skeleton variant="circular" width={30} height={30} />
                  <Skeleton variant="text" width="20%" height={20} />
                  <Skeleton
                    variant="rounded"
                    width={60}
                    height={20}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton variant="text" width="8%" height={20} />
                  <Skeleton
                    variant="rounded"
                    width="20%"
                    height={6}
                    sx={{ borderRadius: 1 }}
                  />
                  <Box
                    sx={{ display: "flex", gap: 0.5, flex: 1 }}
                  >
                    {Array.from({ length: 3 }).map((_, j) => (
                      <Skeleton
                        key={j}
                        variant="rounded"
                        width={70}
                        height={20}
                        sx={{ borderRadius: 1 }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Top KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Total Issues")}
            value={totalBugs}
            icon={<AssignmentIcon sx={{ fontSize: 22 }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Open Issues")}
            value={openBugs}
            icon={<ErrorIcon sx={{ fontSize: 22 }} />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Closed Issues")}
            value={closedBugs}
            icon={<CheckCircleIcon sx={{ fontSize: 22 }} />}
            color="#4CAF50"
            trend={
              totalBugs > 0
                ? Number(((closedBugs / totalBugs) * 100).toFixed(0))
                : 0
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Blocked Issues")}
            value={blockedBugs}
            icon={<BugReportIcon sx={{ fontSize: 22 }} />}
            color="#F44336"
          />
        </Grid>
      </Grid>

      {/* Secondary KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Time Spent")}
            value={projectMetrics.totalTimeSpent}
            unit="hrs"
            icon={<AccessTimeIcon sx={{ fontSize: 22 }} />}
            color="#FF6B6B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Estimated Time")}
            value={projectMetrics.totalEstimated}
            unit="hrs"
            icon={<TimerIcon sx={{ fontSize: 22 }} />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Avg. Resolution")}
            value={avgResolutionTime}
            unit="hrs"
            icon={<TimelineIcon sx={{ fontSize: 22 }} />}
            color="#00BCD4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Assignees")}
            value={assigneeCount}
            icon={<GroupIcon sx={{ fontSize: 22 }} />}
            color="#8E24AA"
          />
        </Grid>
      </Grid>

      {/* Main Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <ChartCard title={t("Tasks by Status")} height={280}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {statusChartData.length > 0 && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={mainPieOptions}
                  series={statusChartData}
                  type="donut"
                  height={250}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <ChartCard title={t("Tasks by Severity")} height={280}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {severityChartData.length > 0 && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={severityBarOptions}
                  series={[{ name: t("Count"), data: severityChartData }]}
                  type="bar"
                  height={250}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <ChartCard title={t("Tasks by Type")} height={280}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {typeChartData.length > 0 && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={typeBarOptions}
                  series={[{ name: t("Count"), data: typeChartData }]}
                  type="bar"
                  height={250}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <ChartCard title={t("Project Progress")} height={280}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {progressData.some((v) => v > 0) && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={progressOptions}
                  series={progressData}
                  type="donut"
                  height={250}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder />
              )}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Workload Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard title={t("Team Workload Distribution")} height={320}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {assigneeWorkloadData.categories.length > 0 &&
              parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={assigneeWorkloadOptions}
                  series={[
                    {
                      name: t("Time Spent"),
                      data: assigneeWorkloadData.timeSpent,
                    },
                    {
                      name: t("Estimated"),
                      data: assigneeWorkloadData.estimated,
                    },
                  ]}
                  type="bar"
                  height={280}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder height={280} />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard title={t("Skill-based Workload")} height={320}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {skillWorkloadData.categories.length > 0 &&
              parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={skillWorkloadOptions}
                  series={[
                    {
                      name: t("Time Spent"),
                      data: skillWorkloadData.timeSpent,
                    },
                    {
                      name: t("Estimated"),
                      data: skillWorkloadData.estimated,
                    },
                  ]}
                  type="bar"
                  height={280}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder height={280} />
              )}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Skills, Tags & Team Role Distribution */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={4}>
          <ChartCard title={t("Issues by Required Skill")} height={320}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {skillChartData.values.length > 0 && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={skillBarOptions}
                  series={[{ name: t("Issues"), data: skillChartData.values }]}
                  type="bar"
                  height={280}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder height={280} />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <ChartCard title={t("Top Tags")} height={320}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {tagChartData.length > 0 && parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={tagBarOptions}
                  series={[{ name: t("Count"), data: tagChartData }]}
                  type="bar"
                  height={280}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder height={280} />
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <ChartCard title={t("Team Role Distribution")} height={320}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {roleDistributionData?.series?.length > 0 &&
              parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={roleDistributionOptions}
                  series={roleDistributionData.series}
                  type="donut"
                  height={280}
                  width="100%"
                />
              ) : (
                <NoDataPlaceholder height={280} />
              )}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* User-wise Task Count */}
      <Grid container spacing={2} sx={{ mb: 10 }}>
        <Grid item xs={12}>
          <ChartCard
            title={t("User-wise Task Assignment")}
            action={
              <TextField
                size="small"
                placeholder={t("Search members...")}
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 280 },
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13px",
                    borderRadius: 2,
                  },
                }}
              />
            }
          >
            <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        py: 1.2,
                        width: "22%",
                      }}
                    >
                      {t("Member")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        py: 1.2,
                        width: "10%",
                      }}
                    >
                      {t("Role")}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        py: 1.2,
                        width: "10%",
                      }}
                    >
                      {t("Tasks")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        py: 1.2,
                        width: "20%",
                      }}
                    >
                      {t("Workload")}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        py: 1.2,
                        width: "38%",
                      }}
                    >
                      {t("Status Breakdown")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member) => (
                      <TableRow
                        key={member.userId}
                        sx={{
                          "&:hover": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.02)",
                          },
                          "&:last-child td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell
                          sx={{ py: 1.2, borderColor: theme.palette.divider }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "12px",
                                fontWeight: 600,
                                bgcolor: theme.palette.primary.main,
                              }}
                            >
                              {member.userName?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  lineHeight: 1.4,
                                }}
                              >
                                {member.userName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "11px" }}
                              >
                                {member.userEmail}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{ py: 1.2, borderColor: theme.palette.divider }}
                        >
                          <Chip
                            label={formatCodeLabel(member.userRole)}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              fontWeight: 600,
                              height: 20,
                              bgcolor:
                                member.userRole === "TEAM_LEAD"
                                  ? "#E3F2FD"
                                  : member.userRole === "MANAGER"
                                    ? "#FFF3E0"
                                    : "#E8F5E9",
                              color:
                                member.userRole === "TEAM_LEAD"
                                  ? "#1565C0"
                                  : member.userRole === "MANAGER"
                                    ? "#E65100"
                                    : "#2E7D32",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ py: 1.2, borderColor: theme.palette.divider }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "15px",
                              color:
                                member.totalBugs > 0
                                  ? theme.palette.text.primary
                                  : theme.palette.text.disabled,
                            }}
                          >
                            {member.totalBugs}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ py: 1.2, borderColor: theme.palette.divider }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={(member.totalBugs / maxBugs) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "grey.700"
                                  : "grey.200",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                bgcolor:
                                  member.totalBugs === 0
                                    ? "transparent"
                                    : member.totalBugs >= maxBugs * 0.7
                                      ? "#F44336"
                                      : member.totalBugs >= maxBugs * 0.4
                                        ? "#FF9800"
                                        : "#4CAF50",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ py: 1.2, borderColor: theme.palette.divider }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {member.statusWiseBugs.length > 0 ? (
                              member.statusWiseBugs.map((sb) => (
                                <Tooltip
                                  key={sb.statusCode}
                                  title={`${sb.status}: ${sb.count}`}
                                  arrow
                                >
                                  <Chip
                                    label={`${sb.status} (${sb.count})`}
                                    size="small"
                                    sx={{
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      height: 20,
                                      bgcolor: `${STATUS_COLORS[sb.statusCode] || "#999"}20`,
                                      color:
                                        STATUS_COLORS[sb.statusCode] || "#999",
                                      border: `1px solid ${STATUS_COLORS[sb.statusCode] || "#999"}40`,
                                    }}
                                  />
                                </Tooltip>
                              ))
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                {t("No tasks")}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {memberSearch
                            ? t("No members found matching your search")
                            : t("No members available")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KanbanDashboard;
